'use client'

/**
 * Interactive companion to the Workflows API docs: the four documented calls
 * (connection check, list workflows, start a run, poll and collect) issued
 * from the browser against the public API, each with the curl it stands for.
 *
 * The key never leaves the tab: it is held in React state only (a reload
 * clears it) and sent solely as the `Authorization` header of requests to the
 * fixed API origin. Nothing here talks to the Martini frontend backend or a
 * session.
 */

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { CopyTextButton } from './DocsCopy'
import { InlineCode } from './DocsPrimitives'

/**
 * The only origin a request may carry the visitor's key to. Fixed on purpose:
 * an editable destination would let a page visitor be talked into sending
 * their key to an attacker's host.
 */
export const PLAYGROUND_API_ORIGIN = 'https://api.martini.film'
const SETTLED = new Set(['completed', 'failed', 'cancelled'])

interface WorkflowInputs {
  variables: Array<{ id: string; name: string; value: string }>
  bins: Array<{
    id: string
    name: string
    assetCount?: number
    required?: boolean
    defaultSource?: 'saved_preset' | 'saved_files' | null
  }>
}
interface PlaygroundWorkflow {
  id: string
  kind: 'saved' | 'placed'
  name: string
  description?: string | null
  fingerprint?: string
  inputs?: WorkflowInputs
  project?: { id: string; name: string }
  canvas?: { id: string; name: string }
}
interface PlaygroundRun {
  id: string
  status: string
  error: string | null
  olives?: { budget: number | null; generation: number; agent: number }
  actions?: Array<{ id: string; name: string; status: string; error: string | null }>
  items?: unknown[]
  openInMartini?: string
}
interface PlaygroundOutput {
  itemId: string
  actionId: string
  assetId: string
  status: 'ready' | 'not_ready' | 'missing'
  filename?: string
  mimeType?: string
  url?: string
  expiresIn?: number
  error?: string
}

interface Exchange {
  curl: string
  status: number | null
  ok: boolean
  body: string
  /** Set when the request never reached the API. */
  transportError?: boolean
}

/** Build the curl a request stands for. The key is always shown as `$MARTINI_API_KEY`. */
export function curlFor(method: string, path: string, body?: unknown): string {
  const lines = [
    `curl${method === 'GET' ? '' : ` -X ${method}`} ${PLAYGROUND_API_ORIGIN}${path} \\`,
    `  -H "Authorization: Bearer $MARTINI_API_KEY"`,
  ]
  if (body !== undefined) {
    lines[lines.length - 1] += ' \\'
    lines.push(`  -H "Content-Type: application/json" \\`)
    lines.push(`  -d ${shellSingleQuoted(JSON.stringify(body))}`)
  }
  return lines.join('\n')
}

/** A POSIX single-quoted word: the only character that needs care inside it is the quote itself. */
function shellSingleQuoted(text: string): string {
  return `'${text.replace(/'/g, `'\\''`)}'`
}

/**
 * The request body for a run from the form's fields. Empty variables are
 * dropped (the workflow's values apply). Every explicitly supplied bin is a
 * replacement, including [] to empty it. Omitted bins retain saved presets or
 * placed canvas contents; required saved bins are validated by the API.
 */
export function runBodyFrom(fields: {
  variables: string
  bins: string
  fingerprint: string
  oliveBudget: string
  idempotencyKey: string
  projectId: string
}): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  const variables = parseObject(fields.variables, 'Variables')
  if (Object.keys(variables).length > 0) body.variables = variables
  const bins = parseObject(fields.bins, 'Bins')
  if (Object.values(bins).some(ids => !Array.isArray(ids) || ids.some(id => typeof id !== 'string'))) {
    throw new Error('Each bin must contain an array of asset IDs. Remove the entry to keep its defaults.')
  }
  if (Object.keys(bins).length > 0) body.bins = bins
  if (fields.fingerprint.trim()) body.fingerprint = fields.fingerprint.trim()
  if (fields.oliveBudget.trim()) body.oliveBudget = Number(fields.oliveBudget)
  if (fields.idempotencyKey.trim()) body.idempotencyKey = fields.idempotencyKey.trim()
  if (fields.projectId.trim()) body.projectId = fields.projectId.trim()
  return body
}

function parseObject(text: string, label: string): Record<string, unknown> {
  let parsed: unknown
  try {
    parsed = JSON.parse(text.trim() || '{}')
  } catch {
    throw new Error(`${label} must be a JSON object.`)
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error(`${label} must be a JSON object.`)
  return parsed as Record<string, unknown>
}

export function WorkflowsApiPlayground() {
  const [apiKey, setApiKey] = useState('')
  const [connection, setConnection] = useState<{ organization: string; key: string } | null>(null)

  const [includePlaced, setIncludePlaced] = useState(false)
  const [workflows, setWorkflows] = useState<PlaygroundWorkflow[] | null>(null)

  const [workflowId, setWorkflowId] = useState('')
  const [variables, setVariables] = useState('{}')
  const [bins, setBins] = useState('{}')
  const [fingerprint, setFingerprint] = useState('')
  const [oliveBudget, setOliveBudget] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [projectId, setProjectId] = useState('')

  const [runId, setRunId] = useState('')
  // The run whose responses may be applied; a poll for another run is dropped.
  const runIdRef = useRef('')
  const [run, setRun] = useState<PlaygroundRun | null>(null)
  const [outputs, setOutputs] = useState<PlaygroundOutput[] | null>(null)
  const [following, setFollowing] = useState(false)
  // Identity of the live follow loop; a loop whose token is no longer current stops without touching shared state.
  const followingRef = useRef<number | null>(null)

  const [exchanges, setExchanges] = useState<Record<string, Exchange>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const request = useCallback(
    async (slot: string, method: 'GET' | 'POST', path: string, body?: unknown) => {
      const curl = curlFor(method, path, body)
      const key = apiKey.trim()
      if (!key) {
        setExchanges(prev => ({
          ...prev,
          [slot]: { curl, status: null, ok: false, body: 'Paste an API key to continue.' },
        }))
        return null
      }
      setBusy(slot)
      try {
        const response = await fetch(`${PLAYGROUND_API_ORIGIN}${path}`, {
          method,
          headers: {
            Authorization: `Bearer ${key}`,
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          },
          body: body === undefined ? undefined : JSON.stringify(body),
        })
        const text = await response.text()
        let json: unknown = null
        try {
          json = JSON.parse(text)
        } catch {
          json = null
        }
        setExchanges(prev => ({
          ...prev,
          [slot]: {
            curl,
            status: response.status,
            ok: response.ok,
            body: json === null ? text : JSON.stringify(json, null, 2),
          },
        }))
        return { ok: response.ok, status: response.status, json }
      } catch {
        setExchanges(prev => ({
          ...prev,
          [slot]: { curl, status: null, ok: false, body: '', transportError: true },
        }))
        return null
      } finally {
        setBusy(null)
      }
    },
    [apiKey],
  )

  const checkConnection = async () => {
    const result = await request('me', 'GET', '/v1/me')
    const me = result?.json as { organization?: { name?: string }; key?: { label?: string } } | null
    setConnection(
      result?.ok && me?.organization?.name ? { organization: me.organization.name, key: me.key?.label ?? 'key' } : null,
    )
  }

  const listWorkflows = async () => {
    const result = await request('list', 'GET', `/v1/workflows${includePlaced ? '?all=true' : ''}`)
    const list = (result?.json as { workflows?: PlaygroundWorkflow[] } | null)?.workflows
    setWorkflows(Array.isArray(list) ? list : null)
  }

  const applyWorkflow = (workflow: PlaygroundWorkflow) => {
    setWorkflowId(workflow.id)
    setFingerprint(workflow.fingerprint ?? '')
    setVariables('{}')
    setBins('{}')
    setProjectId('')
    setFormError(null)
    document.getElementById('playground-run')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const stopFollowing = () => {
    followingRef.current = null
    setFollowing(false)
  }

  const selectRun = (id: string) => {
    runIdRef.current = id
    setRunId(id)
  }

  const startRun = async () => {
    const id = workflowId.trim()
    if (!id) return
    stopFollowing()
    let body: Record<string, unknown>
    try {
      body = runBodyFrom({ variables, bins, fingerprint, oliveBudget, idempotencyKey, projectId })
      setFormError(null)
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'The run body is not valid.')
      return
    }
    const result = await request('run', 'POST', `/v1/workflows/${id}/runs`, body)
    const created = result?.json as PlaygroundRun | null
    if (created?.id) {
      selectRun(created.id)
      setRun(created)
      setOutputs(null)
      document.getElementById('playground-takes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const refreshRun = useCallback(
    async (id: string) => {
      const result = await request('status', 'GET', `/v1/runs/${id}`)
      const fetched = result?.json as PlaygroundRun | null
      if (!fetched?.id || runIdRef.current !== id) return null
      setRun(fetched)
      return fetched
    },
    [request],
  )

  const loadResults = useCallback(
    async (id: string) => {
      const result = await request('status', 'GET', `/v1/runs/${id}/results`)
      if (runIdRef.current !== id) return
      const list = (result?.json as { outputs?: PlaygroundOutput[] } | null)?.outputs
      setOutputs(Array.isArray(list) ? list : null)
    },
    [request],
  )

  const followRun = async () => {
    const id = runId.trim()
    if (!id) return
    if (followingRef.current !== null) {
      stopFollowing()
      return
    }
    const token = Date.now()
    runIdRef.current = id
    followingRef.current = token
    setFollowing(true)
    const mine = () => followingRef.current === token
    let delay = 2000
    let current = await refreshRun(id)
    while (mine() && current && !SETTLED.has(current.status)) {
      await new Promise(resolve => window.setTimeout(resolve, delay))
      delay = Math.min(delay * 1.4, 15000)
      if (!mine()) break
      current = await refreshRun(id)
    }
    // Stopped by the user or superseded by another follow: that follow owns the state now.
    if (!mine()) return
    stopFollowing()
    if (current && SETTLED.has(current.status)) await loadResults(id)
  }
  useEffect(
    () => () => {
      followingRef.current = null
    },
    [],
  )

  return (
    <div className="min-w-0 text-[15px] leading-[1.7] text-foreground/90">
      <header className="mb-10 max-w-3xl">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Workflows API playground</h1>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          The four calls from the{' '}
          <Link href="/docs/workflows" className="font-medium text-foreground underline underline-offset-4">
            Workflows API
          </Link>{' '}
          docs, issued from this page. Each request shows the curl it stands for.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Your key stays in this tab&apos;s memory (a reload clears it) and is sent solely to{' '}
          <InlineCode>{PLAYGROUND_API_ORIGIN}</InlineCode>. Use a key you can revoke afterwards.
        </p>
      </header>

      <div className="space-y-10">
        <Step
          index={1}
          id="playground-connect"
          title="Connect"
          note="Keys come from Settings → Martini API. A key belongs to one workspace."
        >
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <Field label="API key">
              <Input
                type="password"
                value={apiKey}
                onChange={event => setApiKey(event.target.value)}
                placeholder="mtn_live_…"
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Button onClick={checkConnection} disabled={busy !== null}>
              {busy === 'me' ? 'Checking...' : 'Check Connection'}
            </Button>
          </div>
          {connection && (
            <p className="text-sm text-muted-foreground">
              Connected to <span className="text-foreground">{connection.organization}</span> with key{' '}
              <span className="text-foreground">{connection.key}</span>.
            </p>
          )}
          <ExchangeView exchange={exchanges.me} />
        </Step>

        <Step
          index={2}
          id="playground-workflows"
          title="Workflows"
          note="Saved workflows are the library. Placed ones sit on canvases you can edit and need all=true."
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={includePlaced}
                onChange={event => setIncludePlaced(event.target.checked)}
                className="h-3.5 w-3.5 accent-foreground"
              />
              Include placed workflows
            </label>
            <Button onClick={listWorkflows} disabled={busy !== null}>
              {busy === 'list' ? 'Listing...' : 'List Workflows'}
            </Button>
          </div>
          {workflows && workflows.length === 0 && (
            <Empty
              observation="No workflows visible to this key"
              next="Save one to the library, or include placed workflows for canvases you can edit"
            />
          )}
          {workflows && workflows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Workflow</th>
                    <th className="px-3 py-2">Inputs</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {workflows.map(workflow => (
                    <tr key={workflow.id} className="border-t border-border/60 align-top">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full border border-border px-2 text-[11px] text-muted-foreground">
                            {workflow.kind}
                          </span>
                          <span className="text-foreground">{workflow.name || 'Untitled'}</span>
                        </div>
                        {workflow.project?.name && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {workflow.project.name} / {workflow.canvas?.name}
                          </div>
                        )}
                        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground/60">{workflow.id}</div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        <InputsSummary inputs={workflow.inputs} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button variant="outline" size="sm" onClick={() => applyWorkflow(workflow)}>
                          Use
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ExchangeView exchange={exchanges.list} />
        </Step>

        <Step
          index={3}
          id="playground-run"
          title="Run"
          note="A saved workflow uses its saved definition and presets on a fresh canvas. A placed workflow uses that editable copy. Supply only inputs you intend to replace or bins marked required."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Workflow id">
              <Input
                value={workflowId}
                onChange={event => setWorkflowId(event.target.value)}
                placeholder="pick one above"
                spellCheck={false}
              />
            </Field>
            <Field label="Idempotency key" hint="optional">
              <Input
                value={idempotencyKey}
                onChange={event => setIdempotencyKey(event.target.value)}
                placeholder="e.g., job-42"
                spellCheck={false}
              />
            </Field>
            <Field label="Variable overrides" hint="{} keeps saved values; add only variables you want to change">
              <Textarea
                value={variables}
                onChange={event => setVariables(event.target.value)}
                className="min-h-24 font-mono text-xs"
                spellCheck={false}
              />
            </Field>
            <Field
              label="Bin replacements"
              hint="{} keeps defaults. Each named bin replaces its contents; [] empties a media bin. File inputs still require files."
            >
              <Textarea
                value={bins}
                onChange={event => setBins(event.target.value)}
                className="min-h-24 font-mono text-xs"
                spellCheck={false}
              />
            </Field>
            <Field label="Fingerprint" hint="refuses to run if the workflow changed">
              <Input value={fingerprint} onChange={event => setFingerprint(event.target.value)} spellCheck={false} />
            </Field>
            <Field label="Olive budget" hint="optional cap">
              <Input
                type="number"
                min={0}
                value={oliveBudget}
                onChange={event => setOliveBudget(event.target.value)}
                placeholder="e.g., 200"
              />
            </Field>
            <Field
              label="Project id (destination)"
              hint="optional for saved workflows; empty uses Workflow runs. Does not select a source or change presets."
            >
              <Input value={projectId} onChange={event => setProjectId(event.target.value)} spellCheck={false} />
            </Field>
          </div>
          <div className="flex items-center justify-between gap-3">
            {formError ? <p className="text-sm text-destructive">{formError}</p> : <span />}
            <Button onClick={startRun} disabled={busy !== null || following || !workflowId.trim()}>
              {busy === 'run' ? 'Starting...' : 'Start Run'}
            </Button>
          </div>
          <ExchangeView exchange={exchanges.run} />
        </Step>

        <Step
          index={4}
          id="playground-takes"
          title="Takes"
          note="Poll until the run settles. Results answer 409 until then, then list the output files."
        >
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <Field label="Run id">
              <Input
                value={runId}
                onChange={event => {
                  stopFollowing()
                  selectRun(event.target.value)
                }}
                spellCheck={false}
              />
            </Field>
            <Button
              variant="outline"
              onClick={() => runId.trim() && refreshRun(runId.trim())}
              disabled={busy !== null || following || !runId.trim()}
            >
              Refresh
            </Button>
            <Button variant="outline" onClick={followRun} disabled={!runId.trim()}>
              {following ? 'Stop Following' : 'Follow Run'}
            </Button>
            <Button
              onClick={() => runId.trim() && loadResults(runId.trim())}
              disabled={busy !== null || following || !runId.trim()}
            >
              Get Results
            </Button>
          </div>
          {run && <RunSummary run={run} />}
          {outputs && <OutputsTable outputs={outputs} />}
          {!outputs && exchanges.status?.status === 409 && (
            <Empty observation="Still running" next="Follow the run; results load when it settles" />
          )}
          <ExchangeView exchange={exchanges.status} />
        </Step>
      </div>
    </div>
  )
}

function Step({
  index,
  id,
  title,
  note,
  children,
}: {
  index: number
  id: string
  title: string
  note: string
  children: ReactNode
}) {
  return (
    <section id={id} className="grid gap-4 sm:grid-cols-[140px_1fr] sm:gap-6 lg:grid-cols-[200px_1fr] lg:gap-8">
      <aside className="sm:pt-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span className="mr-1.5 text-foreground">{index}</span>
          {title}
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/60">{note}</p>
      </aside>
      <div className="flex min-w-0 flex-col gap-4 rounded-lg border border-border/60 bg-card/20 p-4">{children}</div>
    </section>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-xs text-muted-foreground">
      <span>
        {label}
        {hint && <span className="text-muted-foreground/60"> · {hint}</span>}
      </span>
      {children}
    </label>
  )
}

function InputsSummary({ inputs }: { inputs?: WorkflowInputs }) {
  const variables = inputs?.variables ?? []
  const bins = inputs?.bins ?? []
  if (variables.length === 0 && bins.length === 0) return <span className="text-muted-foreground/60">none</span>
  return (
    <div className="flex flex-col gap-1">
      {variables.length > 0 && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">variables</span>
          {variables.map(variable => (
            <div key={variable.id}>
              {variable.name}
              <span className="text-muted-foreground/60"> = {JSON.stringify(variable.value ?? '')}</span>
            </div>
          ))}
        </div>
      )}
      {bins.length > 0 && (
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">bins</span>
          {bins.map(bin => (
            <div key={bin.id}>
              {bin.name}
              {bin.required === true && <span> · required input</span>}
              {bin.defaultSource === 'saved_preset' && <span> · saved preset (omit to keep)</span>}
              {bin.defaultSource === 'saved_files' && <span> · saved default files (omit to keep)</span>}
              {typeof bin.assetCount === 'number' && (
                <span className="text-muted-foreground/60"> · {bin.assetCount} on canvas</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RunSummary({ run }: { run: PlaygroundRun }) {
  const tone =
    run.status === 'completed'
      ? 'border-foreground bg-foreground text-background'
      : run.status === 'failed' || run.status === 'cancelled'
        ? 'border-destructive text-destructive'
        : run.status === 'running'
          ? 'border-foreground text-foreground'
          : 'border-border text-muted-foreground'
  const takes = run.items?.length ?? 0
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className={cn('rounded-full border px-2.5 py-0.5 text-xs', tone)}>{run.status}</span>
        <span>
          {run.olives?.generation ?? 0} olives generation · {run.olives?.agent ?? 0} agent
          {run.olives?.budget != null ? ` · budget ${run.olives.budget}` : ''}
        </span>
        {takes > 0 && <span>{takes === 1 ? '1 take' : `${takes} takes`}</span>}
        <span className="flex-1" />
        {run.openInMartini && (
          <a
            href={run.openInMartini}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-4"
          >
            Open in Martini
          </a>
        )}
      </div>
      {run.actions && run.actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {run.actions.map(action => (
            <span
              key={action.id}
              title={action.error ?? undefined}
              className="flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-0.5 text-xs text-muted-foreground"
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  action.status === 'completed' && 'bg-foreground',
                  action.status === 'running' && 'animate-pulse bg-foreground',
                  action.status === 'failed' && 'bg-destructive',
                  !['completed', 'running', 'failed'].includes(action.status) && 'bg-muted-foreground/50',
                )}
              />
              {action.name}
            </span>
          ))}
        </div>
      )}
      {run.error && <p className="text-sm text-destructive">{run.error}</p>}
    </div>
  )
}

function OutputsTable({ outputs }: { outputs: PlaygroundOutput[] }) {
  if (outputs.length === 0) return <Empty observation="No outputs" next="The run produced no takes" />
  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Action</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">File</th>
          </tr>
        </thead>
        <tbody>
          {outputs.map(output => (
            <tr key={output.itemId} className="border-t border-border/60">
              <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{output.actionId}</td>
              <td className="px-3 py-2 text-muted-foreground">{output.status}</td>
              <td className="px-3 py-2">
                {output.url ? (
                  <>
                    <a
                      href={output.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-foreground underline underline-offset-4"
                    >
                      {output.filename || output.assetId}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {' '}
                      · {output.mimeType} · link valid {Math.round((output.expiresIn ?? 0) / 60)} min
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">{output.error}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Empty({ observation, next }: { observation: string; next: string }) {
  return (
    <div className="py-6 text-center">
      <p className="text-xs text-muted-foreground">{observation}</p>
      <p className="mt-0.5 text-[10px] text-muted-foreground/60">{next}</p>
    </div>
  )
}

function ExchangeView({ exchange }: { exchange?: Exchange }) {
  // A failed exchange opens by itself; a click overrides that for this exchange only.
  const [toggled, setToggled] = useState<{ exchange: Exchange; open: boolean } | null>(null)
  if (!exchange) return null
  const failed = !exchange.ok
  const open = toggled?.exchange === exchange ? toggled.open : failed
  const setOpen = (next: (value: boolean) => boolean) => setToggled({ exchange, open: next(open) })
  return (
    <div className="border-t border-border/60 pt-3">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex items-center gap-3 text-xs text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
      >
        <span className="text-muted-foreground/60">{open ? '▾' : '▸'}</span>
        <span className={cn('font-mono', failed ? 'text-destructive' : 'text-foreground')}>
          {exchange.transportError
            ? 'Request failed'
            : exchange.status === null
              ? 'Not sent'
              : `HTTP ${exchange.status}`}
        </span>
        <span>Request</span>
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">curl</span>
              <CopyTextButton text={exchange.curl} label="Copy" copiedLabel="Copied" />
            </div>
            <pre className="overflow-x-auto rounded-md border border-border/60 bg-background p-3 font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap break-all">
              {exchange.curl}
            </pre>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground/60">response</div>
            <pre className="max-h-80 overflow-auto rounded-md border border-border/60 bg-background p-3 font-mono text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap break-all">
              {exchange.transportError
                ? 'The request did not reach the API. Check your connection and try again.'
                : exchange.body}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}
